import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { ProfileLink } from "@/components/ProfileDrawer";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateDDMMYYYY, formatNaira } from "@/lib/format";
import { toast } from "sonner";
import { Plus, X, Send, FileSignature, Trash2 } from "lucide-react";

const emptyForm = {
  client_id: "", title: "Sirod Secur — Service Agreement",
  scope_of_services: [], monthly_fee_naira: 4500000,
  start_date: "", end_date: "",
  body: "", signatory_name: "", signatory_email: "",
};

export const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [scopeOptions, setScopeOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const r = await api.get("/contracts");
    setContracts(r.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/clients").then(r => setClients(r.data));
    api.get("/scope-of-services").then(r => setScopeOptions(r.data));
  }, [load]);

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_fee_naira: Number(form.monthly_fee_naira),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
        body: form.body || null,
      };
      await api.post("/contracts", payload);
      toast.success("Contract created");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const sendContract = async (id) => {
    try {
      const r = await api.post(`/contracts/${id}/send`);
      toast.success("Contract sent to client", {
        description: `Mock email to ${r.data.mock_email_sent_to} · share link: ${r.data.sign_link}`,
      });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contract?")) return;
    await api.delete(`/contracts/${id}`);
    toast.success("Contract deleted");
    load();
  };

  const toggleScope = (s) => {
    const has = form.scope_of_services.includes(s);
    setForm({ ...form, scope_of_services: has
      ? form.scope_of_services.filter(x => x !== s)
      : [...form.scope_of_services, s] });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted" style={{ fontSize: 14 }}>{contracts.length} contracts</div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)} data-testid="add-contract">
          <Plus size={14} /> New contract
        </button>
      </div>

      <ContractsTable
        contracts={contracts}
        clientName={clientName}
        onView={setViewing}
        onSend={sendContract}
        onRemove={remove}
      />

      {open && (
        <ContractFormModal
          form={form} setForm={setForm}
          clients={clients} scopeOptions={scopeOptions}
          toggleScope={toggleScope}
          onClose={() => setOpen(false)}
          onSubmit={submit}
        />
      )}

      {viewing && (
        <ContractViewerModal
          contract={viewing}
          clientName={clientName(viewing.client_id)}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

const ContractsTable = ({ contracts, clientName, onView, onSend, onRemove }) => (
  <div className="s-card" style={{ padding: 0 }}>
    <table className="s-table">
      <thead><tr><th>Contract</th><th>Client</th><th>Monthly fee</th><th>Term</th><th>Status</th><th></th></tr></thead>
      <tbody>
        {contracts.map(c => (
          <ContractRow key={c.id} contract={c} clientName={clientName} onView={onView} onSend={onSend} onRemove={onRemove} />
        ))}
      </tbody>
    </table>
  </div>
);

const ContractRow = ({ contract: c, clientName, onView, onSend, onRemove }) => (
  <tr data-testid={`contract-row-${c.id}`}>
    <td>{c.title}</td>
    <td><ProfileLink kind="client" id={c.client_id}>{clientName(c.client_id)}</ProfileLink></td>
    <td className="mono text-teal">{formatNaira(c.monthly_fee_naira)}</td>
    <td className="mono text-muted" style={{ fontSize: 12 }}>
      {formatDateDDMMYYYY(c.start_date)} → {formatDateDDMMYYYY(c.end_date)}
    </td>
    <td><StatusBadge status={c.status} /></td>
    <td>
      <div className="flex gap-2">
        <button onClick={() => onView(c)} className="text-teal" title="View" data-testid={`view-contract-${c.id}`}>
          <FileSignature size={14} />
        </button>
        {c.status === "DRAFT" && (
          <button onClick={() => onSend(c.id)} className="text-teal" title="Send" data-testid={`send-contract-${c.id}`}>
            <Send size={14} />
          </button>
        )}
        <button onClick={() => onRemove(c.id)} className="text-alert" title="Delete">
          <Trash2 size={14} />
        </button>
      </div>
    </td>
  </tr>
);

const ModalShell = ({ onClose, width = 640, children }) => (
  <div onClick={onClose} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex",
    alignItems: "center", justifyContent: "center", zIndex: 100,
  }}>
    <div onClick={(e) => e.stopPropagation()} style={{ width, maxWidth: "95vw", maxHeight: "92vh", overflowY: "auto" }}>
      {children}
    </div>
  </div>
);

const Lbl = ({ children, style }) => (
  <label className="text-muted" style={{ fontSize: 12, ...style }}>{children}</label>
);

const ContractFormModal = ({ form, setForm, clients, scopeOptions, toggleScope, onClose, onSubmit }) => (
  <ModalShell onClose={onClose}>
    <form onSubmit={onSubmit} className="s-card">
      <div className="flex justify-between items-center mb-4">
        <div className="h-section">New contract</div>
        <button type="button" onClick={onClose} className="text-muted"><X size={18} /></button>
      </div>

      <Lbl>Client</Lbl>
      <select required value={form.client_id} onChange={(e) => setForm({...form, client_id: e.target.value})}
              className="s-input mt-1 mb-3" data-testid="ct-client">
        <option value="">Select…</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>

      <Lbl>Contract title</Lbl>
      <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
             className="s-input mt-1 mb-3" />

      <Lbl style={{ display: "block", marginBottom: 6 }}>Scope of services (select all that apply)</Lbl>
      <ScopeChips options={scopeOptions} selected={form.scope_of_services} onToggle={toggleScope} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Lbl>Monthly fee (₦)</Lbl>
          <input type="number" required value={form.monthly_fee_naira}
                 onChange={(e) => setForm({...form, monthly_fee_naira: e.target.value})}
                 className="s-input mt-1 mono" data-testid="ct-fee" />
        </div>
        <div>
          <Lbl>Start date</Lbl>
          <input type="date" required value={form.start_date}
                 onChange={(e) => setForm({...form, start_date: e.target.value})} className="s-input mt-1" />
        </div>
      </div>
      <Lbl style={{ display: "block", marginTop: 12 }}>End date</Lbl>
      <input type="date" required value={form.end_date}
             onChange={(e) => setForm({...form, end_date: e.target.value})} className="s-input mt-1 mb-3" />

      <Lbl>Signatory name (client)</Lbl>
      <input value={form.signatory_name} onChange={(e) => setForm({...form, signatory_name: e.target.value})}
             className="s-input mt-1 mb-3" placeholder="Full legal name" />
      <Lbl>Signatory email</Lbl>
      <input type="email" value={form.signatory_email}
             onChange={(e) => setForm({...form, signatory_email: e.target.value})}
             className="s-input mt-1 mb-3" />

      <Lbl>Contract body (leave blank to auto-generate from scope + fee)</Lbl>
      <textarea value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
                className="s-input mt-1 mb-4 mono" rows={6}
                placeholder="Auto-generated using the selected scope and term. Edit if you need custom clauses." />

      <button type="submit" className="btn-primary w-full" data-testid="ct-submit">Create contract</button>
    </form>
  </ModalShell>
);

const ScopeChips = ({ options, selected, onToggle }) => (
  <div className="flex flex-wrap gap-2 mb-3">
    {options.map(s => {
      const active = selected.includes(s);
      return (
        <button type="button" key={s} onClick={() => onToggle(s)}
                className={active ? "btn-primary" : "btn-outline"}
                style={{ padding: "6px 12px", fontSize: 12 }}
                data-testid={`scope-${s}`}>
          {s}
        </button>
      );
    })}
  </div>
);

const ContractViewerModal = ({ contract, clientName, onClose }) => (
  <ModalShell onClose={onClose} width={720}>
    <div className="s-card">
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="h-section">{contract.title}</div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>{clientName}</div>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={contract.status} />
          <button onClick={onClose} className="text-muted"><X size={18} /></button>
        </div>
      </div>
      <pre className="mono" style={{
        fontSize: 12, whiteSpace: "pre-wrap", color: "#fff",
        background: "#0D1B2A", padding: 16, borderRadius: 8, lineHeight: 1.6,
      }}>{contract.body}</pre>
      {contract.signature && <SignatureBlock signature={contract.signature} signedAt={contract.signed_at} />}
    </div>
  </ModalShell>
);

const SignatureBlock = ({ signature, signedAt }) => (
  <div className="s-card mt-4">
    <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>SIGNED</div>
    <div style={{ fontSize: 14, color: "#fff" }}>{signature.full_name}</div>
    <div className="mono text-teal" style={{ fontSize: 18, marginTop: 6 }}>{signature.signature_text}</div>
    <div className="text-muted mono" style={{ fontSize: 11, marginTop: 6 }}>{signedAt}</div>
  </div>
);
